import isArray from 'lodash/isArray';
import omit from 'lodash/omit';

import { useReactQueryState } from '../../../common/reactQuery/hooks/useReactQueryState';

import {
  FetchProductsCacheKey,
  FetchProductsFilterFields,
  FetchProductsFilters,
  FetchProductsSort
} from '../../productsTypes';

import { INITIAL_PRODUCTS_SORT } from '../../productsConstants';

const defaultIgnoredFilterNames = [
  FetchProductsFilterFields.PRODUCT_CATEGORY_ID,
  FetchProductsFilterFields.PRODUCT_PARENT_CATEGORY_IDS
];

const pureInitialFilters = { nda: false, blocked: false };

const pureInitialSort = INITIAL_PRODUCTS_SORT;

interface ProductsFiltersCacheOptions {
  cacheKey: FetchProductsCacheKey;
  initialFilters?: FetchProductsFilters;
  initialSort?: FetchProductsSort;
  ignoredFilterNames?: FetchProductsFilterFields[];
}

function useProductsFiltersCache({
  cacheKey,
  initialFilters = pureInitialFilters,
  initialSort = pureInitialSort,
  ignoredFilterNames = defaultIgnoredFilterNames
}: ProductsFiltersCacheOptions) {
  const filtersCacheKey = `${cacheKey}-filters`;
  const sortCacheKey = `${cacheKey}-sort`;

  const { value: cachedProductsFilters, setValue: setCachedProductsFilters } =
    useReactQueryState<FetchProductsFilters>(filtersCacheKey, initialFilters);

  const { value: cachedProductsSort, setValue: setCachedProductsSort } =
    useReactQueryState<FetchProductsSort>(sortCacheKey, initialSort);

  return {
    defaultInitialFilters: initialFilters,
    defaultInitialSort: initialSort,
    cachedProductsFilters,
    cachedProductsSort,
    changeCachedProductsFilters: (
      changedFilters: FetchProductsFilters,
      removedFilterNames: string[] = []
    ) => {
      setCachedProductsFilters(
        omit({ ...cachedProductsFilters, ...changedFilters }, [
          ...(isArray(removedFilterNames) ? removedFilterNames : []),
          ...ignoredFilterNames
        ])
      );
    },
    setCachedProductsFilters: (nextFilters: FetchProductsFilters) => {
      setCachedProductsFilters(omit(nextFilters, ignoredFilterNames));
    },
    resetCachedProductsFiltersAndSort: (
      onResetFilters?: (initialFilters: FetchProductsFilters) => void,
      onResetSort?: (initialSort: FetchProductsSort) => void
    ) => {
      setCachedProductsFilters(initialFilters);
      onResetFilters?.(initialFilters);
      setCachedProductsSort(initialSort);
      onResetSort?.(initialSort);
    },
    setCachedProductsSort
  };
}

export default useProductsFiltersCache;
